/**
 * FCM/APNs 직발송 — 토큰 등록 + 수신 처리.
 *
 * <h3>흐름</h3>
 * - `bootstrapFcmPush()`   : App init 시점. 리스너만 붙인다(권한 요청 X).
 * - `registerPushDevice()` : 로그인 후. 권한 요청 → 토큰 확보 → BE `POST /devices` 로 회원 매핑.
 * - `unregisterPushDevice()`: 로그아웃 시. 기기 행을 내려 이전 회원의 푸시가 계속 오지 않게 한다.
 *
 * 권한 거부 시 fallback: 인앱 인박스(NotificationCenter) 만 동작한다.
 *
 * <h3>표시는 OS 에 맡긴다</h3>
 * 앱은 알림을 직접 그리지 않는다. 포그라운드 수신 시에도 트레이 적재는 OS 가 하고, 여기서는
 * 인박스 캐시만 무효화한다. 배너가 필요하면 별도 기획으로 다룬다.
 *
 * <h3>`register()` 를 반드시 부른다</h3>
 * 픽쿠리의 같은 모듈은 `register()` 를 부르지 않는다 — 거기는 OneSignal SDK 가 토큰을 이미 쥐고
 * 있어서 부르면 토큰이 하나 더 생기고 알림이 두 번 온다. peeka 는 SDK 를 걷어냈으니 토큰을
 * 받아올 주체가 여기뿐이다. <b>이 차이를 지우면 토큰이 영영 안 생긴다.</b>
 *
 * <h3>BE 계약</h3>
 * ```
 * POST /api/v1/user/devices
 *   { installationId, platform: 'IOS'|'AND', deviceToken?, notificationAgreementYn }
 * → { deviceSerialNo, notificationAgreementYn }
 * ```
 * 기기를 가리키는 키는 `installationId` 다 — 토큰은 회전하므로 키로 쓸 수 없다.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { isNative, getPlatform, nativeWarn } from "./index";
import { client } from "../api/client";
import { getDeviceId } from "./liveUpdate";
import { useAuthStore } from "../store/authStore";
import { usePendingDeepLinkStore } from "../store/pendingDeepLinkStore";
import { NOTIFICATIONS_KEY } from "../hooks/useNotificationQueries";

export type DeviceRegisterRequest = {
  installationId: string;
  platform: "IOS" | "AND" | "WEB";
  deviceToken?: string;
  notificationAgreementYn: "Y" | "N";
};

export type DeviceRegisterResponse = {
  deviceSerialNo: number;
  notificationAgreementYn: "Y" | "N";
};

export type NotificationOpenedHandler = (
  deepLink: string | null,
  payload?: { kind?: string; data?: Record<string, unknown> },
) => void;

type PushData = Record<string, unknown>;

const TOKEN_WAIT_MS = 8_000;

let bootstrapped = false;
let openedHandler: NotificationOpenedHandler | null = null;
let foregroundReceivedHandler: (() => void) | null = null;
/** 마지막으로 발급받은 푸시 토큰. `registration` 이벤트로만 채워진다. */
let pushToken: string | null = null;
/** 마지막으로 BE 에 등록 성공한 기기 매핑 — 로그아웃 해제·디버그 스냅샷용. */
let lastDeviceRegister: DeviceRegisterResponse | null = null;

function str(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

function platformCode(): DeviceRegisterRequest["platform"] {
  const p = getPlatform();
  if (p === "android") return "AND";
  if (p === "ios") return "IOS";
  return "WEB";
}

/**
 * App init 시 1회 호출. 리스너만 붙이고 권한은 요청하지 않는다(로그인 후 registerPushDevice 에서).
 * native 가 아니면 no-op.
 */
export async function bootstrapFcmPush(opts?: {
  onNotificationOpened?: NotificationOpenedHandler;
  onForegroundReceived?: () => void;
}): Promise<void> {
  // ⚠ 핸들러 주입은 early-return 보다 앞이고, 준 것만 덮어쓴다.
  //   registerPushDevice 가 리스너 보장을 위해 인자 없이 먼저 부를 수 있는데, 그때 null 로
  //   덮거나 bootstrapped 로 막아 버리면 뒤늦은 RootLayout 호출이 핸들러를 못 붙여
  //   딥링크가 조용히 죽는다.
  if (opts?.onNotificationOpened) openedHandler = opts.onNotificationOpened;
  if (opts?.onForegroundReceived) foregroundReceivedHandler = opts.onForegroundReceived;

  if (!isNative() || bootstrapped) return;

  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");

    await PushNotifications.addListener("registration", (t) => {
      pushToken = t.value;
    });

    await PushNotifications.addListener("registrationError", (err) => {
      nativeWarn("fcm:registrationError", err);
    });

    // 포그라운드 수신. 표시는 OS 가 하므로 화면은 건드리지 않는다.
    await PushNotifications.addListener("pushNotificationReceived", () => {
      try {
        // 인박스 뱃지/목록 갱신은 알림 종류와 무관하게 항상.
        foregroundReceivedHandler?.();
      } catch (err) {
        // 리스너에서 던지면 이후 알림 처리까지 죽는다. 삼키고 로그만 남긴다.
        nativeWarn("fcm:received", err);
      }
    });

    // 알림 탭(백그라운드·종료 상태 포함).
    await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      try {
        const data = (action.notification.data ?? {}) as PushData;
        openedHandler?.(str(data.deepLink) ?? null, { kind: str(data.kind), data });
        // tap 후 인박스 진입 시점에 신선한 데이터가 보이도록 invalidate.
        foregroundReceivedHandler?.();
      } catch (err) {
        nativeWarn("fcm:actionPerformed", err);
      }
    });

    bootstrapped = true;
  } catch (err) {
    nativeWarn("fcm:bootstrap", err);
  }
}

/**
 * 로그인 직후 호출. 권한 요청 → 토큰 확보 → BE 등록.
 *
 * 권한이 거부돼도 등록은 진행한다 — Android 는 권한 없이도 FCM 토큰이 발급되고, 사용자가
 * 나중에 설정에서 알림을 켜면 재등록 없이 바로 도달한다. iOS 는 토큰이 안 오면 `deviceToken`
 * 없이 행만 남고, 그 기기는 발송 대상에서 자연히 빠진다(BE `findSendableTokens`).
 */
export async function registerPushDevice(): Promise<DeviceRegisterResponse | null> {
  if (!isNative()) return null;

  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const { granted } = await getNativePushPermission();
    if (!granted) {
      const result = await PushNotifications.requestPermissions();
      if (result.receive !== "granted") {
        console.warn("[fcm] permission not granted — register anyway (옵트인은 추후 권한 ON 시)");
      }
    }

    // 토큰은 registration 이벤트로만 온다 — 리스너가 붙어 있어야 받는다.
    await bootstrapFcmPush();
    await PushNotifications.register();
    const token = await waitForToken(TOKEN_WAIT_MS);

    const installationId = await getDeviceId();
    if (!installationId) {
      // 설치 식별자는 기기 행의 키다. 없으면 등록 자체가 성립하지 않는다.
      console.warn("[fcm] installationId unavailable — skip device register, retry later");
      return null;
    }

    const { granted: nowGranted } = await getNativePushPermission();
    const body: DeviceRegisterRequest = {
      installationId,
      platform: platformCode(),
      deviceToken: token ?? undefined,
      // 실제 OS 권한 상태를 반영 — 거부면 N (서비스/거래성 알림은 BE 가 bypass 처리).
      notificationAgreementYn: nowGranted ? "Y" : "N",
    };

    const res = await client.post<DeviceRegisterResponse>("/devices", body);
    lastDeviceRegister = res.data;
    return res.data;
  } catch (err) {
    nativeWarn("fcm:registerPushDevice", err);
    return null;
  }
}

/**
 * 로그아웃 시 호출. 기기 행을 내린다.
 *
 * ⚠ 액세스 토큰이 아직 살아 있을 때만 호출할 수 있다(authStore.logout 의 clear 이전).
 * 직발송은 기기 토큰이 곧 타겟이라 종전 OneSignal 의 alias 해제 같은 원격 정리가 없다 —
 * 이걸 빠뜨리면 로그아웃한 기기가 이전 회원의 푸시를 계속 받는다.
 */
export async function unregisterPushDevice(): Promise<void> {
  const deviceSerialNo = lastDeviceRegister?.deviceSerialNo;
  lastDeviceRegister = null;
  if (!isNative() || !deviceSerialNo) return;
  try {
    await client.delete(`/devices/${deviceSerialNo}`);
  } catch (err) {
    nativeWarn("fcm:unregisterPushDevice", err);
  }
}

/** 디버그 메뉴용 푸시 등록 스냅샷 — 발송이 안 닿을 때 어느 단계가 비었는지 보기 위함. */
export type PushDebugSnapshot = {
  native: boolean;
  bootstrapped: boolean;
  permissionGranted: boolean;
  canRequestPermission: boolean;
  pushToken: string | null;
  installationId: string | null;
  deviceSerialNo: number | null;
  deviceAgreementYn: "Y" | "N" | null;
};

export async function getPushDebugSnapshot(): Promise<PushDebugSnapshot> {
  const base: PushDebugSnapshot = {
    native: isNative(),
    bootstrapped,
    permissionGranted: false,
    canRequestPermission: false,
    pushToken,
    installationId: null,
    deviceSerialNo: lastDeviceRegister?.deviceSerialNo ?? null,
    deviceAgreementYn: lastDeviceRegister?.notificationAgreementYn ?? null,
  };
  if (!base.native) return base;
  try {
    const { granted, canRequest } = await getNativePushPermission();
    return {
      ...base,
      permissionGranted: granted,
      canRequestPermission: canRequest,
      installationId: (await getDeviceId()) ?? null,
    };
  } catch (err) {
    nativeWarn("fcm:debugSnapshot", err);
    return base;
  }
}

// ---------------------------------------------------------------------------
// Permission helpers (low-level) — graceful no-op on web.
// ---------------------------------------------------------------------------

/**
 * 현재 OS 알림 권한 상태 조회.
 *   - granted: 이미 허용됨
 *   - canRequest: OS 다이얼로그를 아직 띄울 수 있음 (한 번도 물어본 적 없음)
 *
 * Capacitor 의 `receive` 는 `granted | denied | prompt | prompt-with-rationale` 4값이고,
 * `prompt*` 가 곧 "물어볼 수 있음" 이다.
 */
export async function getNativePushPermission(): Promise<{ granted: boolean; canRequest: boolean }> {
  if (!isNative()) return { granted: false, canRequest: false };
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const { receive } = await PushNotifications.checkPermissions();
    return { granted: receive === "granted", canRequest: receive.startsWith("prompt") };
  } catch (err) {
    nativeWarn("fcm:getPermission", err);
    return { granted: false, canRequest: false };
  }
}

/** OS 알림 권한 요청. 이미 거부된 상태면 다이얼로그가 안 뜨고 그대로 denied 가 돌아온다. */
export async function requestNativePushPermission(): Promise<boolean> {
  if (!isNative()) return false;
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const { receive } = await PushNotifications.requestPermissions();
    return receive === "granted";
  } catch (err) {
    nativeWarn("fcm:requestPermission", err);
    return false;
  }
}

// ---------------------------------------------------------------------------
// internals
// ---------------------------------------------------------------------------

/** registration 이벤트가 늦게 올 수 있어 짧게 polling. */
async function waitForToken(timeoutMs: number): Promise<string | null> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (pushToken) return pushToken;
    await sleep(200);
  }
  return null;
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/** 테스트 전용 — 모듈 상태 초기화. */
export function __resetFcmPushForTest(): void {
  bootstrapped = false;
  openedHandler = null;
  foregroundReceivedHandler = null;
  pushToken = null;
  lastDeviceRegister = null;
}

// ---------------------------------------------------------------------------
// React lifecycle hook — RootLayout 에서 한 번 호출.
// ---------------------------------------------------------------------------

/**
 * RootLayout 안에서 호출. App init 시 리스너 bootstrap + accessToken 변화에 따라 기기 등록.
 *
 * 푸시 deep link 가 오면 react-router 의 navigate 로 점프한다.
 */
export function useFcmPushLifecycle(): void {
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const queryClient = useQueryClient();

  useEffect(() => {
    void bootstrapFcmPush({
      onNotificationOpened: (deepLink) => {
        if (!deepLink) return;
        const path = resolveDeepLinkPath(deepLink);
        if (!path) return;

        // Killed cold-boot race: 알림 탭이 콜드스타트 부트스트랩(/auth/refresh) 완료보다 빠를 수
        // 있다 → navigate 가 protected route 가드에 막혀 /login 으로 빠지면 deepLink 손실.
        // 부트스트랩 settle 전이면 큐잉하고, RootLayout 의 useDeepLinkReplay 가 settle 시 consume.
        if (!useAuthStore.getState().bootstrapped) {
          usePendingDeepLinkStore.getState().setPendingDeepLink(path);
          return;
        }
        navigate(path);
      },
      onForegroundReceived: () => {
        queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      },
    });
  }, [navigate, queryClient]);

  useEffect(() => {
    if (!accessToken) return;
    void registerPushDevice();
  }, [accessToken]);
}

/**
 * deepLink 를 react-router path 로 정규화.
 *   - `template://notifications/123` → `/notifications/123`
 *   - `/notifications/123`           → `/notifications/123`
 *
 * ⚠ 알림 템플릿의 딥링크 스킴은 `template://` 이다(NT_NOTI_TMPL_MST 의 실제 값). OS 앱링크용
 * `peeka://` 와는 다른 층이다 — 여기 오는 값은 전자다.
 */
export function resolveDeepLinkPath(deepLink: string): string | null {
  if (!deepLink) return null;
  try {
    const u = new URL(deepLink);
    if (u.protocol === "template:") {
      return `/${u.host}${u.pathname}${u.search}`;
    }
  } catch {
    // not a full URL — assume path.
  }
  return deepLink.startsWith("/") ? deepLink : `/${deepLink}`;
}
